/**
 * bd-affinity.js
 *
 * First-party "browse affinity" profile: what product category is this visitor
 * actually shopping? Customers on this store overwhelmingly stay inside one
 * department (floor tile, LVP, mosaics, …), so the department they browse is a
 * usable prediction of what they want to see next.
 *
 * The profile lives in the visitor's own browser (localStorage) — nothing is
 * sent to a server by this file, and no cross-site identifier is created. It is
 * read by:
 *   - assets/bd-personalized-picks.js  (swaps a homepage/landing-page carousel)
 *   - assets/bd-view-tracking.js       (reports the affinity to GA4 via GTM)
 *   - any section markup carrying [data-bd-affinity-show] / [-hide]
 *
 * Signals come from snippets/bd-visitor-context.liquid, which publishes the
 * current page's taxonomy tags as window.BDPageContext. Facets are the store's
 * existing tag scheme (docs/catalog-import-guide.md):
 *   cat-*      department        (cat-floor, cat-lvp, cat-mosaic, …)
 *   subcat-*   sub-category      (subcat-12x24, subcat-wood-look, …)
 *   material-* / look-* / use-*  attribute affinities
 *   pcat-*     legacy category page (kept last — sparse, 107 of them)
 * plus `collection:<handle>` and `type:<product type>`.
 *
 * Scoring is a recency-weighted count: each view adds its weight, and every
 * score halves after HALF_LIFE_DAYS. A visitor who looked at LVP a month ago
 * and mosaics this morning reads as a mosaics shopper.
 *
 * Consent: if the visitor chose "Essential only" in the cookie notice, the
 * profile is held in memory for the page and never written to storage.
 *
 * Public API (window.BDAffinity):
 *   .record(kind, facets)       kind: 'product' | 'collection' | 'cart'
 *   .scores(prefix)             { facet: decayed score }
 *   .ranked(prefix)             [{ key, score }] high to low
 *   .top(prefix)                highest scoring facet, or null
 *   .topN(prefix, n)
 *   .match(candidates, minScore)  best { keys: [...] } candidate, or null
 *   .focus()                    0-1 share of score held by the top department
 *   .depth()                    number of retained signals
 *   .recentProductIds()         ids behind the "Recently viewed" section
 *   .reset()                    forget everything
 *   .ready                      true once the page's signals are recorded
 * Event: document 'on:bd-affinity:ready' with { department, top, focus, depth }.
 */
(function () {
  'use strict';

  var PROFILE_KEY = 'bd_affinity_v1';
  var CONSENT_KEY = 'bd_cookie_consent_v1'; // written by bd-cookie-banner.js
  var RECENT_KEY = 'cc-recently-viewed'; // written by layout/theme.liquid

  // Ordered by usefulness: the slice below keeps the most meaningful facets
  // when a product carries a long tag list.
  var FACET_PREFIXES = ['cat-', 'subcat-', 'material-', 'look-', 'use-', 'pcat-'];

  var WEIGHTS = { product: 3, collection: 2, cart: 8 };
  var HALF_LIFE_DAYS = 21;
  var MAX_EVENTS = 80;
  var MAX_AGE_DAYS = 120;
  var MAX_FACETS_PER_EVENT = 12;
  var DAY_MS = 86400000;

  // A single product view is the floor for "this visitor has an interest".
  var DEFAULT_MIN_SCORE = WEIGHTS.product;

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- storage ------------------------------------------------- */

  var memoryProfile = null; // used when the visitor declined non-essential cookies

  function readStored(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  /**
   * False only when the visitor actively declined. An undecided visitor is
   * treated the same way the existing "Recently viewed" list treats them: the
   * profile is first-party, functional, and never leaves the browser.
   */
  function persistenceAllowed() {
    if (readStored(CONSENT_KEY) === 'essential') return false;

    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (api && typeof api.currentVisitorConsent === 'function') {
      var consent = api.currentVisitorConsent() || {};
      if (consent.preferences === 'no') return false;
    }

    return true;
  }

  function load() {
    if (!persistenceAllowed()) return memoryProfile || { v: 1, events: [] };

    var profile = null;
    try {
      profile = JSON.parse(readStored(PROFILE_KEY) || 'null');
    } catch (e) {
      profile = null;
    }

    if (!profile || profile.v !== 1 || !Array.isArray(profile.events)) {
      profile = { v: 1, events: [] };
    }

    return profile;
  }

  function prune(events) {
    var cutoff = Date.now() - MAX_AGE_DAYS * DAY_MS;

    return events
      .filter(function (event) {
        return event && typeof event.t === 'number' && event.t > cutoff && Array.isArray(event.f);
      })
      .sort(function (a, b) {
        return b.t - a.t;
      })
      .slice(0, MAX_EVENTS);
  }

  function save(profile) {
    profile.events = prune(profile.events);

    if (!persistenceAllowed()) {
      memoryProfile = profile;
      return;
    }

    try {
      window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch (e) {
      // Full or disabled storage: keep the page's view of the profile only.
      memoryProfile = profile;
    }
  }

  function reset() {
    memoryProfile = null;
    try {
      window.localStorage.removeItem(PROFILE_KEY);
    } catch (e) {
      /* nothing to clear */
    }
  }

  /* ---------- facets -------------------------------------------------- */

  function normalizeKey(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase();
  }

  /** Taxonomy tags only — free-text merchandising tags are ignored. */
  function facetsFromTags(tags) {
    var out = [];
    if (!Array.isArray(tags)) return out;

    FACET_PREFIXES.forEach(function (prefix) {
      tags.forEach(function (tag) {
        var key = normalizeKey(tag);
        if (key.indexOf(prefix) !== 0) return;
        if (out.indexOf(key) === -1) out.push(key);
      });
    });

    return out.slice(0, MAX_FACETS_PER_EVENT);
  }

  /** Facets for the page the visitor is on right now. */
  function facetsFromContext(context) {
    var ctx = context || window.BDPageContext || {};
    var facets = [];

    function add(key) {
      var normalized = normalizeKey(key);
      if (normalized && facets.indexOf(normalized) === -1) facets.push(normalized);
    }

    if (ctx.product) {
      facetsFromTags(ctx.product.tags).forEach(add);
      if (ctx.product.type) add('type:' + ctx.product.type);
    }

    if (ctx.collection) {
      facetsFromTags(ctx.collection.tags).forEach(add);
      if (ctx.collection.handle) add('collection:' + ctx.collection.handle);
    }

    return facets.slice(0, MAX_FACETS_PER_EVENT);
  }

  /* ---------- recording ----------------------------------------------- */

  function record(kind, facets) {
    var weight = WEIGHTS[kind];
    if (!weight || designMode) return;

    var list = (facets || []).map(normalizeKey).filter(Boolean);
    if (!list.length) return;

    var profile = load();
    profile.events.unshift({ t: Date.now(), k: kind, w: weight, f: list.slice(0, MAX_FACETS_PER_EVENT) });
    save(profile);
  }

  /* ---------- scoring ------------------------------------------------- */

  function scores(prefix) {
    var totals = {};
    var now = Date.now();

    load().events.forEach(function (event) {
      var ageDays = (now - event.t) / DAY_MS;
      var decayed = event.w * Math.pow(0.5, ageDays / HALF_LIFE_DAYS);

      event.f.forEach(function (key) {
        if (prefix && key.indexOf(prefix) !== 0) return;
        totals[key] = (totals[key] || 0) + decayed;
      });
    });

    return totals;
  }

  function ranked(prefix) {
    var totals = scores(prefix);

    return Object.keys(totals)
      .map(function (key) {
        return { key: key, score: totals[key] };
      })
      .sort(function (a, b) {
        return b.score - a.score;
      });
  }

  function top(prefix) {
    var list = ranked(prefix);
    return list.length ? list[0].key : null;
  }

  function topN(prefix, count) {
    return ranked(prefix).slice(0, count || 3);
  }

  /** Share of department score held by the strongest department (0-1). */
  function focus() {
    var departments = ranked('cat-');
    if (!departments.length) return 0;

    var total = departments.reduce(function (sum, entry) {
      return sum + entry.score;
    }, 0);

    return total > 0 ? departments[0].score / total : 0;
  }

  function depth() {
    return load().events.length;
  }

  /**
   * Pick the best of a set of candidates, each declaring the facet keys it
   * serves: match([{ keys: ['cat-floor'], handle: 'floor-tile' }, …]).
   * A candidate scores as its strongest key. Ties keep the earlier candidate,
   * so merchandising order in the theme editor breaks them.
   */
  function match(candidates, minScore) {
    var totals = scores(null);
    var threshold = typeof minScore === 'number' && minScore >= 0 ? minScore : DEFAULT_MIN_SCORE;
    var best = null;

    (candidates || []).forEach(function (candidate) {
      var score = 0;

      (candidate.keys || []).forEach(function (key) {
        var value = totals[normalizeKey(key)] || 0;
        if (value > score) score = value;
      });

      if (score >= threshold && (!best || score > best.score)) {
        best = { candidate: candidate, score: score };
      }
    });

    return best ? best.candidate : null;
  }

  function recentProductIds() {
    try {
      var items = JSON.parse(readStored(RECENT_KEY) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  }

  /* ---------- DOM hooks ----------------------------------------------- */

  /**
   * Publishes the affinity onto <html> so CSS and sections can react:
   *   data-bd-affinity="cat-floor"   top department
   *   class="bd-aff-cat-floor …"     top facets overall
   */
  function publish() {
    var root = document.documentElement;
    var department = top('cat-');

    if (department) {
      root.setAttribute('data-bd-affinity', department);
    } else {
      root.removeAttribute('data-bd-affinity');
    }

    topN(null, 6).forEach(function (entry) {
      root.classList.add('bd-aff-' + entry.key.replace(/[^a-z0-9-]+/g, '-'));
    });
  }

  function keyList(value) {
    return String(value || '')
      .split(',')
      .map(normalizeKey)
      .filter(Boolean);
  }

  function interested(keys, minScore) {
    return !!match([{ keys: keys }], minScore);
  }

  /**
   * Show/hide blocks by interest. Author the element with `hidden` when using
   * data-bd-affinity-show so it never flashes for the wrong visitor:
   *   <div hidden data-bd-affinity-show="cat-lvp,cat-laminate">…</div>
   * In the theme editor everything is revealed so it can be edited.
   */
  function applyVisibilityRules(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-bd-affinity-show]').forEach(function (el) {
      var show = designMode || interested(keyList(el.getAttribute('data-bd-affinity-show')), scoreAttr(el));
      el.hidden = !show;
    });

    root.querySelectorAll('[data-bd-affinity-hide]').forEach(function (el) {
      var hide = !designMode && interested(keyList(el.getAttribute('data-bd-affinity-hide')), scoreAttr(el));
      el.hidden = hide;
    });
  }

  function scoreAttr(el) {
    var raw = parseFloat(el.getAttribute('data-bd-affinity-min-score'));
    return isNaN(raw) ? DEFAULT_MIN_SCORE : raw;
  }

  /* ---------- init ---------------------------------------------------- */

  function recordPage(context) {
    var ctx = context || window.BDPageContext || {};
    var facets = facetsFromContext(ctx);
    if (!facets.length) return;

    if (ctx.pageType === 'product') {
      record('product', facets);
    } else if (ctx.pageType === 'collection') {
      record('collection', facets);
    }
  }

  var api = {
    record: record,
    recordPage: recordPage,
    facetsFromTags: facetsFromTags,
    facetsFromContext: facetsFromContext,
    scores: scores,
    ranked: ranked,
    top: top,
    topN: topN,
    focus: focus,
    depth: depth,
    match: match,
    interested: interested,
    applyVisibilityRules: applyVisibilityRules,
    recentProductIds: recentProductIds,
    reset: reset,
    profile: load,
    weights: WEIGHTS,
    ready: false
  };

  window.BDAffinity = api;

  function init() {
    recordPage();
    publish();
    applyVisibilityRules();

    api.ready = true;
    document.dispatchEvent(
      new CustomEvent('on:bd-affinity:ready', {
        detail: {
          department: top('cat-'),
          top: topN(null, 5),
          focus: focus(),
          depth: depth()
        }
      })
    );
  }

  // An add to cart is the strongest signal available on the storefront. The
  // facets come from the page it happened on, which is the product page for a
  // normal add and the collection page for a quick add — right either way.
  document.addEventListener('on:cart:add', function () {
    record('cart', facetsFromContext());
    publish();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
